import { englishError } from "../utils/databaseErrors";
// src/components/TreatmentTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { validMarbleQuantity } from "../utils/marbleQuantity";

const treatLabel = { none: "None", epoxy: "Epoxy", resin: "Resin" };
const treatMap   = { epoxy: "Epoxy", resin: "Resin", none: "None" };

// Fallback لتوليد الكود لو shown_code مش موجود
function codeWithFallback(row) {
  if (row?.shown_code) return row.shown_code;
  const blockCode = row.block_code || "";
  const f = row.face_treatment || "none";
  const b = row.back_treatment || "none";
  const p = row.is_polished ? "P" : "";
  const short = [
    f === "epoxy" ? "FE" : f === "resin" ? "FR" : "",
    b === "epoxy" ? "BE" : b === "resin" ? "BR" : "",
    p,
  ].join("");
  return short ? `${blockCode}-${short}` : blockCode;
}

function TreatmentTab({ user }) {
  const showroomId = Number(user?.showroom_id);

  // data
  const [stockRows, setStockRows]   = useState([]); // من v_stock_display
  const [blocks, setBlocks]         = useState([]); // للمواد/الموردين
  const [materials, setMaterials]   = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [lastError, setLastError]   = useState("");

  // form
  const [selectedStockId, setSelectedStockId] = useState("");
  const [moveSlabs, setMoveSlabs]             = useState("");
  const [face, setFace]                       = useState("none");
  const [back, setBack]                       = useState("none");
  const [polished, setPolished]               = useState(false);
  const [suffix, setSuffix]                   = useState("");

  // table filters
  const [scope, setScope] = useState("this"); // "this" | "all"
  const [includeZero, setIncludeZero] = useState(false);
  const [ignoreShowroomFilterOnce, setIgnoreShowroomFilterOnce] = useState(false); // زر تجربة

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  async function fetchAll() {
    setLoading(true);
    setLastError("");
    try {
      // 1) جرّب الفيو أولاً
      const { data: vstock, error: vErr } = await supabase
        .from("v_stock_display")  // مهم: schema
        .select("*");

      if (vErr) {
        console.warn("v_stock_display error:", vErr);
        setLastError(`v_stock_display error: ${englishError(vErr.message)}`);
      }
      console.log("v_stock_display rows:", vstock?.length ?? 0);

      // 2) لو مفيش بيانات من الفيو، اعمل Fallback
      let vRows = vstock || [];
      if (!vRows.length) {
        console.warn("No rows from view. Falling back to joins…");
        const [
          { data: bd, error: e1 },
          { data: blks, error: e2 },
          { data: mats, error: e3 },
          { data: custs, error: e4 },
        ] = await Promise.all([
          supabase.from("block_distribution").select("*"),
          supabase.from("block").select("id, code, material_id, customer_id, width, height"),
          supabase.from("material").select("id, name"),
          supabase.from("customer").select("id, name"),
        ]);

        if (e1 || e2 || e3 || e4) {
          const msg = [e1?.message, e2?.message, e3?.message, e4?.message].filter(Boolean).join(" | ");
          console.error("fallback SELECT error:", msg);
          setLastError(`fallback error: ${msg}`);
        }

        const matLookup = Object.fromEntries((mats || []).map(m => [m.id, m.name || "-"]));
        const custLookup = Object.fromEntries((custs || []).map(c => [c.id, c.name || "-"]));
        const blockLookup = Object.fromEntries((blks || []).map(b => [b.id, b]));

        // حول block_distribution -> شكل v_stock_display المتوقع
        vRows = (bd || []).map(d => {
          const b = blockLookup[d.block_id] || {};
          const shown_code =
            (d.variant_suffix && d.variant_suffix.trim())
              ? `${b.code || ""}-${d.variant_suffix.trim()}`
              : null; // هيتم توليده بالـ fallback
          return {
            stock_id: d.id,
            showroom_id: d.showroom_id,
            block_id: d.block_id,
            block_code: b.code,
            variant_id: d.id, // تقريبًا
            variant_suffix: d.variant_suffix || "",
            shown_code, // ممكن تبقى null ونعمل fallback
            slabs_count: Number(d.slabs_count || 0),
            face_treatment: d.face_treatment || "none",
            back_treatment: d.back_treatment || "none",
            is_polished: !!d.is_polished,
            // للعرض (هنرجع نجيب البلوك نفسه تحت)
            _material_id: b.material_id,
            _customer_id: b.customer_id,
            _width: b.width,
            _height: b.height,
            _material_name: matLookup[b.material_id] || "-",
            _customer_name: custLookup[b.customer_id] || "-",
          };
        });

        setBlocks(blks || []);
        setMaterials(mats || []);
        setCustomers(custs || []);
      } else {
        // عندك بيانات من الفيو: حمّل جداول الربط (عرض فقط)
        const [
          { data: blks, error: e2 },
          { data: mats, error: e3 },
          { data: custs, error: e4 },
        ] = await Promise.all([
          supabase.from("block").select("id, code, material_id, customer_id, width, height"),
          supabase.from("material").select("id, name"),
          supabase.from("customer").select("id, name"),
        ]);
        if (e2 || e3 || e4) {
          const msg = [e2?.message, e3?.message, e4?.message].filter(Boolean).join(" | ");
          console.warn("side tables load error:", msg);
        }
        setBlocks(blks || []);
        setMaterials(mats || []);
        setCustomers(custs || []);
      }

      setStockRows(vRows);
      console.log("options (raw):", vRows.length);
    } finally {
      setLoading(false);
    }
  }

  const matLookup  = useMemo(
    () => Object.fromEntries((materials||[]).map(m => [m.id, m.name || "-"])),
    [materials]
  );
  const custLookup = useMemo(
    () => Object.fromEntries((customers||[]).map(c => [c.id, c.name || "-"])),
    [customers]
  );
  const blockLookup = useMemo(
    () => Object.fromEntries((blocks||[]).map(b => [b.id, b])),
    [blocks]
  );

  // قائمة الاختيار من صالة المستخدم (>0)
  const sourceOptions = useMemo(() => {
    let rows = (stockRows || []).filter(r => Number(r.slabs_count) > 0);

    // زر تجربة: تجاهل فلتر المعرض مرّة واحدة
    if (!ignoreShowroomFilterOnce) {
      rows = rows.filter(r => Number(r.showroom_id) === showroomId);
    }

    const opts = rows.map(r => {
      const b = blockLookup[r.block_id] || {};
      return {
        stock_id: r.stock_id,
        variant_id: r.variant_id,
        block_id: r.block_id,
        block_code: r.block_code,
        shownCode: codeWithFallback(r),
        material: r._material_name || matLookup[b.material_id] || "-",
        supplier: r._customer_name || custLookup[b.customer_id] || "-",
        available: Number(r.slabs_count),
        face: r.face_treatment || "none",
        back: r.back_treatment || "none",
        polished: !!r.is_polished,
        width: b.width,
        height: b.height,
      };
    });

    console.log("options (after showroom & qty>0):", opts.length, "| showroom:", showroomId, "| ignoreShowroomFilterOnce:", ignoreShowroomFilterOnce);
    return opts.sort(
      (a, b) =>
        (a.material || "").localeCompare(b.material || "") ||
        (a.block_code || "").localeCompare(b.block_code || "")
    );
  }, [stockRows, showroomId, blockLookup, matLookup, custLookup, ignoreShowroomFilterOnce]);

  const selected = useMemo(
    () => sourceOptions.find((o) => String(o.stock_id) === String(selectedStockId)),
    [sourceOptions, selectedStockId]
  );

  // suffix التلقائي (من غير Dash)
  useEffect(() => {
    const f = face === "none" ? "N" : face === "epoxy" ? "E" : "R";
    const b = back === "none" ? "N" : back === "epoxy" ? "E" : "R";
    const p = polished ? "P" : "";
    setSuffix(`${f}${b}${p}`);
  }, [face, back, polished]);

  async function handleSplit(e) {
    e.preventDefault();
    if (!selected) return alert("Select a stock item first");
    const qty = Number(moveSlabs || 0);
    if (!validMarbleQuantity(qty)) return alert("Enter a positive slab quantity with up to three decimal places.");
    if (qty > selected.available) return alert("Quantity exceeds showroom stock");

    setLoading(true);
    try {
      const { data: newVariantId, error } = await supabase.rpc(
        "split_variant_stock",
        {
          p_stock_id: Number(selected.stock_id),
          p_move_slabs: Number(qty),
          p_face: face,
          p_back: back,
          p_polished: polished,
          p_suffix: suffix || "",
        }
      );

      if (error) {
        const msg = String(englishError(error.message) || "");
        if (msg.includes("STOCK_NOT_FOUND"))   return alert("Stock was not found!");
        if (msg.includes("QTY_EXCEEDS_STOCK")) return alert("Quantity exceeds available stock!");
        if (msg.includes("INVALID_TREATMENT")) return alert("Invalid treatment selection!");
        return alert("Unable to split stock: " + englishError(error.message));
      }

      alert(`Created Variant New (ID: ${newVariantId}) and stock updated`);
      setSelectedStockId("");
      setMoveSlabs("");
      setFace("none");
      setBack("none");
      setPolished(false);
      await fetchAll();
    } finally {
      setLoading(false);
    }
  }

  // styles
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    background: "#faf8f4",
    borderRadius: 10,
    direction: "ltr",
    textAlign: "left",
    fontSize: 12,
  };
  const thtd = {
    borderBottom: "1px solid #d2cbc1",
    padding: "6px 8px",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  };

  return (
    <div className="ui-legacy-page" dir="ltr" style={{ maxWidth: 1100, margin: "auto", padding: 18 }}>
      <h2 style={{ color: "var(--accent, #514536)", marginBottom: 8 }}>Treatment (Split blocks)</h2>
      <div style={{ color: "#716759", marginBottom: 12, fontSize: 13 }}>
        Showroom: <b>{user?.showroom_name || user?.showroom_id}</b>
      </div>

      {lastError && (
        <div className="ui-card" style={{ background:"#e6dec8", border:"1px solid #d2cbc1", color:"#514536", padding:8, borderRadius:8, marginBottom:10 }}>
          ⚠️ Note: {lastError}
        </div>
      )}

      {/* فورم التنفيذ */}
      <form className="ui-form"
        onSubmit={handleSplit}
        style={{
          background: "#faf8f4",
          borderRadius: 10,
          padding: 12,
          boxShadow: "0 1px 10px #51453610",
          marginBottom: 16,
        }}
      >
        <div className="ui-fields">
<div className="ui-field"><span>Select an item (variant in showroom):</span><div className="ui-toolbar" style={{ display:"flex", gap:8, alignItems:"center" }}>
            <select
              value={selectedStockId}
              onChange={(e) => setSelectedStockId(e.target.value)}
              style={{
                minWidth: 320,
                padding: "6px 8px",
                borderRadius: 8,
                border: "1px solid #d2cbc1",
                background: "#faf8f4",
              }}
            >
              <option value="">— Select —</option>
              {sourceOptions.map((o) => (
                <option key={o.stock_id} value={o.stock_id}>
                  {o.shownCode} — {o.material} — Available: {o.available} slabs
                </option>
              ))}
            </select>
            <button className="ui-secondary"
              type="button"
              onClick={() => setIgnoreShowroomFilterOnce(s => !s)}
              style={{ padding:"6px 10px", borderRadius:8, border:"1px solid #d2cbc1", background:"#faf8f4" }}
              title="Show all showrooms temporarily"
            >
              {ignoreShowroomFilterOnce ? "Back to current showroom" : "Show all (Test)"}
            </button>
          </div></div>
<div className="ui-field"><span>Slabs to treat:</span><input
            type="number"
            min={0.001}
            step="0.001"
            max={selected?.available || 1}
            value={moveSlabs}
            onChange={(e) => setMoveSlabs(e.target.value)}
            style={{
              width: 140,
              padding: "6px 8px",
              borderRadius: 8,
              border: "1px solid #d2cbc1",
              background: "#faf8f4",
            }}
            placeholder={`Maximum: ${selected?.available || 0}`}
          /></div>
<div className="ui-field"><span>Front:</span><div>
            <label style={{ marginLeft: 12 }}>
              <input type="radio" name="face" checked={face === "epoxy"} onChange={() => setFace("epoxy")} /> Epoxy
            </label>
            <label style={{ marginLeft: 12 }}>
              <input type="radio" name="face" checked={face === "resin"} onChange={() => setFace("resin")} /> Resin
            </label>
            <label>
              <input type="radio" name="face" checked={face === "none"} onChange={() => setFace("none")} /> None
            </label>
          </div></div>
<div className="ui-field"><span>Back:</span><div>
            <label style={{ marginLeft: 12 }}>
              <input type="radio" name="back" checked={back === "epoxy"} onChange={() => setBack("epoxy")} /> Epoxy
            </label>
            <label style={{ marginLeft: 12 }}>
              <input type="radio" name="back" checked={back === "resin"} onChange={() => setBack("resin")} /> Resin
            </label>
            <label>
              <input type="radio" name="back" checked={back === "none"} onChange={() => setBack("none")} /> None
            </label>
          </div></div>
<div className="ui-field"><span>Polished:</span><div>
            <label>
              <input type="checkbox" checked={polished} onChange={(e) => setPolished(e.target.checked)} /> Polished
            </label>
          </div></div>
<div className="ui-field"><label htmlFor="treatment-suffix">Suffix for the new code (optional)</label><input id="treatment-suffix"
            value={suffix}
            onChange={(e) => setSuffix(e.target.value)}
            placeholder="Example: FE (None -)"
            style={{
              width: 180,
              padding: "6px 8px",
              borderRadius: 8,
              border: "1px solid #d2cbc1",
            }}
          /><div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ opacity: 0.7 }}>Preview:</span>
            <code style={{ background: "#f2ede4", padding: "2px 6px", borderRadius: 6 }}>
              {selected?.block_code}{suffix ? "-" + suffix : ""}
            </code>
          </div></div>
</div>

        {selected && (
          <div style={{ marginTop: 10, fontSize: 13, color: "var(--accent, #514536)" }}>
            <div>
              Current treatment: Front <b>{treatLabel[selected.face]}</b> | Back{" "}
              <b>{treatLabel[selected.back]}</b> | {selected.polished ? "Polished" : "Unpolished"}
            </div>
            <div style={{ color: "#716759" }}>
              Slab dimensions (Reference): {selected.width || "-"} × {selected.height || "-"}
            </div>
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <button className="ui-primary"
            type="submit"
            disabled={loading || !selectedStockId || !moveSlabs}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              background: "var(--accent, #514536)",
              color: "#faf8f4",
              fontWeight: "bold",
              border: "none",
              cursor: "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Processing..." : "Apply treatment (Split)"}
          </button>
        </div>
      </form>

      {/* فلاتر عرض للجدول */}
      <div className="ui-toolbar"
        style={{
          background: "#faf8f4",
          borderRadius: 10,
          padding: "10px 12px",
          boxShadow: "0 1px 10px #51453610",
          marginBottom: 10,
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ fontWeight: "bold", color: "var(--accent, #514536)" }}>Width:</div>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="radio" name="scope" checked={scope === "this"} onChange={() => setScope("this")} />
          Current user's showroom only
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="radio" name="scope" checked={scope === "all"} onChange={() => setScope("all")} />
          All showrooms
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginInlineStart: 16 }}>
          <input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)} />
          Show items with zero stock
        </label>
      </div>

      {/* جدول العرض */}
      <div className="ui-table-scroll"><table style={tableStyle}>
        <thead>
          <tr style={{ background: "#f2ede4" }}>
            <th style={thtd}>No.</th>
            <th style={thtd}>Code</th>
            <th style={thtd}>Material</th>
            <th style={thtd}>Supplier</th>
            <th style={thtd}>Showroom</th>
            <th style={thtd}>Available (slabs)</th>
            <th style={thtd}>Treatment</th>
          </tr>
        </thead>
        <tbody>
          {sourceOptions.map((o, idx) => (
            <tr key={o.stock_id}>
              <td style={thtd}>{idx + 1}</td>
              <td style={{ ...thtd, fontWeight: "bold" }}>{o.shownCode}</td>
              <td style={thtd}>{o.material}</td>
              <td style={thtd}>{o.supplier}</td>
              <td style={thtd}>{o.block_id ? showroomId : "-"}</td>
              <td style={thtd}>{o.available}</td>
              <td style={thtd}>
                Front: {treatMap[o.face]} | Back: {treatMap[o.back]} | {o.polished ? "Polished" : "Unpolished"}
              </td>
            </tr>
          ))}
          {sourceOptions.length === 0 && (
            <tr>
              <td style={thtd} colSpan={7}>
                No records match the filters.
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
    </div>
  );
}

export default TreatmentTab;
